import PersonIcon from '@mui/icons-material/Person';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getBusiness } from 'lib/api';
import { createSigner } from 'lib/api/users';
import { CreateSignerBody } from 'lib/api/users/types';
import {
  COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PROFESSION_OPTIONS,
} from 'lib/consts/usersFields';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import {
  buildSignerValidation,
  isEnumKey,
  isValidChileanPhone,
  isValidEmail,
  validaRut,
} from 'lib/functions/utils';
import React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

type Signer = Omit<CreateSignerBody, 'businessIdentifier' | 'businessName'>;

type NewSigner = Signer;

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  businessIdentifier?: string;
};

const CreateSigner: React.FC<Props> = ({
  open,
  setOpen,
  businessIdentifier,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [newSigner, setNewSigner] = useState<NewSigner | undefined>(undefined);

  const { hasError: emailHasError, message: emailErrorMessage } =
    buildSignerValidation(
      !newSigner?.email?.length,
      !!newSigner?.email?.length && !isValidEmail(newSigner?.email),
      'Email obligatorio',
      'Email inválido',
    );

  const { hasError: nameHasError, message: nameErrorMessage } =
    buildSignerValidation(
      !newSigner?.name?.length,
      false,
      'Nombre obligatorio',
      'Nombre inválido',
    );

  const { hasError: identifierHasError, message: identifierErrorMessage } =
    buildSignerValidation(
      !newSigner?.identifier?.length,
      !!newSigner?.identifier?.length && !validaRut(newSigner.identifier),
      'RUT obligatorio',
      'RUT inválido',
    );

  const { hasError: phoneHasError, message: phoneErrorMessage } =
    buildSignerValidation(
      !newSigner?.phone?.length,
      !!newSigner?.phone?.length && !isValidChileanPhone(newSigner.phone),
      'Teléfono obligatorio',
      'Teléfono inválido (569XXXXXXXX)',
    );

  const { hasError: countryHasError, message: countryErrorMessage } =
    buildSignerValidation(
      !newSigner?.country?.length,
      !!newSigner?.country?.length &&
        !isEnumKey(COUNTRY_OPTIONS, newSigner.country),
      'País obligatorio',
      'País inválido',
    );

  const { hasError: professionHasError, message: professionErrorMessage } =
    buildSignerValidation(
      !newSigner?.profession?.length,
      false,
      'Profesión obligatoria',
      'Profesión inválida',
    );

  const { hasError: maritalHasError, message: maritalErrorMessage } =
    buildSignerValidation(
      !newSigner?.maritalStatus?.length,
      !!newSigner?.maritalStatus?.length &&
        !isEnumKey(MARITAL_STATUS_OPTIONS, newSigner.maritalStatus),
      'Estado civil obligatorio',
      'Estado civil inválido',
    );

  const hasErrors =
    emailHasError ||
    nameHasError ||
    phoneHasError ||
    identifierHasError ||
    countryHasError ||
    professionHasError ||
    maritalHasError;

  const handleSaveSigner = async () => {
    try {
      if (!businessIdentifier) {
        return showAlert(dispatch, 'error', 'Problemas al crear el firmante');
      }
      if (hasErrors) {
        let errorMessage = '';
        if (emailHasError) errorMessage = emailErrorMessage;
        else if (nameHasError) errorMessage = nameErrorMessage;
        else if (phoneHasError) errorMessage = phoneErrorMessage;
        else if (identifierHasError) errorMessage = identifierErrorMessage;
        else if (countryHasError) errorMessage = countryErrorMessage;
        else if (professionHasError) errorMessage = professionErrorMessage;
        else if (maritalHasError) errorMessage = maritalErrorMessage;
        showAlert(dispatch, 'error', errorMessage);
        return;
      }
      setLoading(true);
      const business = await getBusiness(dispatch, businessIdentifier);
      await createSigner(dispatch, {
        ...newSigner,
        businessIdentifier,
        businessName: business?.name,
      });
      void queryClient.invalidateQueries({
        queryKey: ['businessProfile', 'signers', businessIdentifier],
      });
      showAlert(dispatch, 'success', 'Signer created successfully');
      setOpen(false);
      setNewSigner(undefined);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewSigner((prev: NewSigner | undefined) =>
      prev
        ? {
            ...prev,
            [e.target.name]: e.target.value,
          }
        : { [e.target.name]: e.target.value },
    );
  };

  const handleAutocompleteChange =
    (field: keyof NewSigner) =>
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: NewSigner[keyof NewSigner] | null,
    ) => {
      setNewSigner((prev: NewSigner | undefined) =>
        prev
          ? {
              ...prev,
              [field]: value ?? prev[field],
            }
          : { [field]: value },
      );
    };

  const getLabel = (value: string, options: Record<string, string>) => {
    return options[value] ?? '';
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle
        onClose={() => setOpen(false)}
        title="Crear firmante"
        icon={<PersonIcon color="primary" />}
        color="primary"
      />
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label="Email"
              name="email"
              value={newSigner?.email}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={emailHasError}
              helperText={emailHasError ? emailErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nombre"
              name="name"
              value={newSigner?.name}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={nameHasError}
              helperText={nameHasError ? nameErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Teléfono"
              name="phone"
              value={newSigner?.phone}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={phoneHasError}
              helperText={phoneHasError ? phoneErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="RUT"
              name="identifier"
              value={newSigner?.identifier}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={identifierHasError}
              helperText={identifierHasError ? identifierErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              options={Object.keys(COUNTRY_OPTIONS)}
              value={newSigner?.country}
              onChange={handleAutocompleteChange('country')}
              getOptionLabel={(option) => getLabel(option, COUNTRY_OPTIONS)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nacionalidad"
                  margin="dense"
                  fullWidth
                  error={countryHasError}
                  helperText={countryHasError ? countryErrorMessage : ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              options={PROFESSION_OPTIONS}
              value={newSigner?.profession}
              onChange={handleAutocompleteChange('profession')}
              onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setNewSigner((prev: NewSigner | undefined) =>
                    prev
                      ? {
                          ...prev,
                          profession: newInputValue,
                        }
                      : { profession: newInputValue },
                  );
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Profesión"
                  margin="dense"
                  fullWidth
                  error={professionHasError}
                  helperText={professionHasError ? professionErrorMessage : ''}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value) {
                      setNewSigner((prev: NewSigner | undefined) =>
                        prev
                          ? {
                              ...prev,
                              profession: value,
                            }
                          : { profession: value },
                      );
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              options={Object.keys(MARITAL_STATUS_OPTIONS)}
              value={newSigner?.maritalStatus}
              onChange={handleAutocompleteChange('maritalStatus')}
              getOptionLabel={(option) =>
                getLabel(option, MARITAL_STATUS_OPTIONS)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Estado civil"
                  margin="dense"
                  fullWidth
                  error={maritalHasError}
                  helperText={maritalHasError ? maritalErrorMessage : ''}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={handleSaveSigner}
          loading={loading}
        >
          Crear
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSigner;
